import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import {
  FormScheduleDetailedDto,
  FormSchedulesClient,
  FormScheduleUpdateDto,
  FormTemplateClient,
  TransitionRequestDto,
  UnityScheduleType,
  UnityWorkflowState,
} from '@unity/clients';
import { TextareaControlComponent } from '@unity/components/forms/textarea-control/textarea-control.component';
import { TextboxReadonlyControlComponent } from '@unity/components/forms/textbox-readonly-control/textbox-readonly-control.component';
import { FormLayoutComponent } from '@unity/components/layouts/form-layout/form-layout.component';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { DropdownFormSchemasControlComponent } from '@unity/forms/dropdown-form-schemas-control/dropdown-form-schemas-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { DateTimezonePipe } from '@unity/pipes/date-timezone.pipe';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { GridFormInstanceSharedComponent } from '@unity/shared/grid-form-instance-shared/grid-form-instance-shared.component';
import { BannerAlertSharedComponent } from '@unity/shared/presentation/banner-alert-shared/banner-alert-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { nonZeroFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

class FormScheduleUpdateSchema implements FormGroupControlSchema<FormScheduleUpdateDto> {
  public name: FormControl<string>;
  public description: FormControl<string>;
  public form_schema_id: FormControl<number>;

  public constructor(formSchedule?: FormScheduleDetailedDto) {
    this.name = stringFormControl({ disabled: true, value: formSchedule?.name });
    this.description = stringFormControl({ disabled: true, value: formSchedule?.description });
    this.form_schema_id = nonZeroFormControl({ disabled: true, value: formSchedule?.form_schema.id });
  }
}

@Component({
  selector: 'unity-manage-form-schedule-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    KENDO_SVGICON,
    KENDO_BUTTON,
    DateTimezonePipe,
    ReactiveFormsModule,
    PageContainerComponent,
    TextboxReadonlyControlComponent,
    TextboxControlComponent,
    TextareaControlComponent,
    AuthorizeDirective,
    FormLayoutComponent,
    BannerAlertSharedComponent,
    PanelManageSharedComponent,
    GridFormInstanceSharedComponent,
    ActionDeleteSharedComponent,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
    DropdownFormSchemasControlComponent,
  ],
  templateUrl: './manage-form-schedule-page.component.html',
  styleUrl: './manage-form-schedule-page.component.scss',
})
export class ManageFormSchedulePageComponent extends AbstractManageComponent<
  FormScheduleDetailedDto,
  FormScheduleUpdateDto,
  FormScheduleUpdateSchema
> {
  private readonly formSchedulesClient = inject(FormSchedulesClient);
  private readonly formSchemasClient = inject(FormTemplateClient);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $formId = input.required({ alias: 'formId', transform: (value: string) => parseInt(value) });

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      const agreementId = this.$agreementId();
      return agreementId && resourceId ? { agreementId, resourceId } : undefined;
    },
    stream: ({ params: { agreementId, resourceId } }) => this.formSchedulesClient.getOneForAgreementAsDetailed(agreementId, resourceId),
  });

  private formSchemasResource = rxResource({
    stream: () => this.formSchemasClient.getAllAsMini(),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<FormScheduleUpdateSchema>) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.formSchedulesClient.updateOneForAgreement(agreementId, resourceId, form.getRawValue());
  };

  protected override deleteResourceFn$ = (resourceId: number) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.formSchedulesClient.deleteOneForAgreement(agreementId, resourceId);
  };

  protected readonly $formSchemas = computed(() => this.formSchemasResource.value()?.data ?? []);

  protected override availableTransitionsFn$ = (resourceId: number) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.availableTransitionsClient.getAllForFormScheduleAsMini(agreementId, resourceId);
  };

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.transitionsClient.setForFormSchedule(agreementId, resourceId, transition);
  };

  protected override newControlsFn(dto?: FormScheduleDetailedDto) {
    return new FormScheduleUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'FormSchedule:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'FormSchedule:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'FormSchedule:Delete';
  protected override resourceName = 'Form Template';
  protected override $resourceId = this.$formId;
  protected readonly $contentHeader = computed(() => `Form Template: ${this.$resource()?.name}`);
  protected readonly $contentHeaderSecondary = computed(() => `Agreement: ${this.$resource()?.agreement.name}`);

  protected readonly $isDraft = computed(() => this.$resource()?.current_state.enum_value == UnityWorkflowState.Draft);
  protected readonly $isDisabled = computed(() => this.$resource()?.current_state.enum_value == UnityWorkflowState.Disabled);
  protected readonly $isDeletable = computed(() => this.$resource()?.form_type.is_deletable === true);

  protected readonly $createFormInstanceAction = computed(() => {
    const formSchedule = this.$resource();
    const agreementId = this.$agreementId();
    if (!formSchedule || !agreementId || formSchedule.type.enum_value === UnityScheduleType.Adhoc) {
      return;
    }
    return QuickActions.CreateFormInstance(agreementId, formSchedule.id);
  });
  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));
}
